import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import PaginatorMixin from '@/helpers/vue_mixins/v2/PaginatorMixin';
import Paginator from '@/components/v2/Paginator.vue';
import BaseSubtitle from '@/components/v2/BaseSubtitle';
import MaintenanceLogs from '@/components/v2/MaintenanceLogs';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import ActionButton from '@/components/action_button/Index.vue';
import GraphqlErrorMixin from '@/helpers/vue_mixins/GraphqlErrorMixin';
import { Mutation } from 'vuex-class';
import AppMaintenanceLogs from '@/plugins/graphql/queries/v2/app-maintenance-logs.gql';
import MaintenanceLogger from '@/plugins/graphql/mutations/v2/app-maintenance-logger.gql';
import { PER_PAGE } from '@/helpers/constants/app';
import AppBasic from '@/plugins/graphql/queries/v2/app-basic.gql';
import { AppBasicModel } from '@/helpers/entities/models/AppBasicModel';
import Avatar from '@/components/avatar/Index.vue';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import { StoreStatus } from '@/helpers/entities/enums/v2/StoreStatus';
import SegmentAnalyticsMixin from '@/helpers/vue_mixins/v2/SegmentAnalyticsMixin';
import RouterParamsAppMixin from '../../helpers/vue_mixins/RouterParamsAppMixin';
/**
 * Maintenance Page
 * @author Peter John Alvarado <peterjohn@gorated.ph>
 * @since Version {0.1.13}
 */
let SettingsPageSubscription = class SettingsPageSubscription extends mixins(GraphqlErrorMixin, PaginatorMixin, RouterParamsAppMixin, ViewportSizeMixin, SegmentAnalyticsMixin) {
    constructor() {
        super(...arguments);
        this.maintenance = false;
        this.cancelled = false;
    }
    /**
     * Maintenance Switch on Change event handler
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.1.13}
     *
     * @param {boolean} switch value
     *
     * @return {void} no return value
     */
    onChange(val) {
        this.maintenance = val;
        if (val) {
            this.showConfirmationDialog();
        }
        else if (!val && !this.cancelled) {
            this.log(val);
        }
    }
    /**
     * Confirm Dialog cancel event handler
     * Revert Switch Value
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.1.13}
     *
     * @return {void}
     */
    onCancel() {
        this.maintenance = !this.maintenance;
    }
    /**
     * Send logger mutation and handler mutation status for button loaders
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {1.0.0}
     *
     * @param {boolean} switch value
     *
     * @return {void} no return value
     */
    log(val) {
        this.mutationRequestOnFlight();
        this.sendMutation(!!val).finally(() => {
            this.hideConfirmationDialog();
            this.requestDone();
        });
    }
    /**
     * Store Status Mapper Based on Maintenace State
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.21.0}
     */
    storeStatusFor(maintenance) {
        if (maintenance) {
            return StoreStatus.Closed;
        }
        return StoreStatus.Active;
    }
    /**
     * Method for sending maintenance logger mutation request
     * and updating apollo cache for maintenance logs and app status
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.1.13}
     *
     * @param {boolean} switch value
     *
     * @return {void} no return value
     */
    async sendMutation(val) {
        const appUuid = this.appUUID;
        const storeStatus = this.storeStatusFor(val);
        return this.$apollo.mutate({
            mutation: MaintenanceLogger,
            variables: {
                input: {
                    appUuid: this.appUUID,
                    maintenance: val,
                },
            },
            update(cache, { data }) {
                const { maintenanceLog } = data.appMaintenanceLogger;
                const appQuery = {
                    query: AppBasic,
                    variables: { appUuid },
                };
                const { app } = cache.readQuery(appQuery);
                cache.writeQuery({
                    ...appQuery,
                    data: {
                        app: {
                            ...app,
                            storeStatus,
                            maintenance: maintenanceLog.maintenance,
                        },
                    },
                });
                const queryOptions = {
                    query: AppMaintenanceLogs,
                    variables: { appUuid, first: PER_PAGE },
                };
                const { appMaintenanceLogs } = cache.readQuery(queryOptions);
                cache.writeQuery({
                    ...queryOptions,
                    data: {
                        appMaintenanceLogs: {
                            edges: [
                                { node: maintenanceLog, __typename: 'MaintenanceLog' },
                                ...appMaintenanceLogs.edges,
                            ],
                            __typename: 'MaintenanceLogEdges',
                            pageInfo: appMaintenanceLogs.pageInfo,
                            totalCount: appMaintenanceLogs.totalCount + 1,
                        },
                    },
                });
            },
        });
    }
    trackSegment(merchant) {
        const path = 'Maintenance';
        const eventName = `Dashboard - ${path} View`;
        this.segmentTrack(eventName, { merchant });
    }
};
__decorate([
    Mutation('hideConfirmationDialog')
], SettingsPageSubscription.prototype, "hideConfirmationDialog", void 0);
__decorate([
    Mutation('showConfirmationDialog')
], SettingsPageSubscription.prototype, "showConfirmationDialog", void 0);
__decorate([
    Mutation('requestOnFlight')
], SettingsPageSubscription.prototype, "mutationRequestOnFlight", void 0);
__decorate([
    Mutation('requestDone')
], SettingsPageSubscription.prototype, "requestDone", void 0);
SettingsPageSubscription = __decorate([
    Component({
        apollo: {
            app: {
                query: AppBasic,
                variables() {
                    const vm = this;
                    return { appUuid: vm.appUUID };
                },
                update(data) {
                    const vm = this;
                    const app = new AppBasicModel(data.app);
                    vm.trackSegment(app?.name);
                    return new AppBasicModel(app);
                },
                result({ data }) {
                    const vm = this;
                    const app = new AppBasicModel(data.app);
                    vm.maintenance = app.maintenance;
                },
            },
        },
        components: {
            ActionButton,
            BaseSubtitle,
            ConfirmationDialog,
            MaintenanceLogs,
            Paginator,
            Avatar,
        },
    })
], SettingsPageSubscription);
export default SettingsPageSubscription;
